import React, { useEffect, useState } from "react";
import { collection, doc, getDoc, getDocs, query, where } from "firebase/firestore";
import { Alert, Badge, Button, Card, Spinner } from "react-bootstrap";

import { db } from "../../../shared/libs/firebaseConfig";
import { useAuth } from "../../../shared/auth/AuthProvider";
import { AFFILIATE_COLLECTIONS, AFFILIATE_STATUSES } from "../../../shared/utils/affiliateSchema";

const SITE_ORIGIN = "https://chicchiclawtutor.com";

function getCourseCover(course = {}) {
  return (
    course?.imageCoverUrl ||
    course?.coverImage ||
    course?.thumbnailURL ||
    course?.thumbnail ||
    ""
  );
}

async function copyTextToClipboard(text = "") {
  if (navigator?.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return;
  }

  const textarea = document.createElement("textarea");
  textarea.value = text;
  textarea.setAttribute("readonly", "");
  textarea.style.position = "fixed";
  textarea.style.opacity = "0";
  document.body.appendChild(textarea);
  textarea.select();
  document.execCommand("copy");
  document.body.removeChild(textarea);
}

export default function AffiliateLinksPage() {
  const { user } = useAuth();
  const uid = user?.uid || "";

  const [loading, setLoading] = useState(true);
  const [affiliate, setAffiliate] = useState(null);
  const [courses, setCourses] = useState([]);
  const [error, setError] = useState("");
  const [copiedCourseId, setCopiedCourseId] = useState("");

  useEffect(() => {
    if (!uid) return;
    const loadData = async () => {
      try {
        setLoading(true);
        const affSnap = await getDoc(doc(db, AFFILIATE_COLLECTIONS.affiliates, uid));
        const aff = affSnap.exists() ? { id: affSnap.id, ...affSnap.data() } : null;
        setAffiliate(aff);

        if (aff?.status === AFFILIATE_STATUSES.approved) {
          const courseSnap = await getDocs(
            query(collection(db, "courses"), where("affiliateEnabled", "==", true))
          );
          setCourses(courseSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
        }
      } catch (e) {
        console.error(e);
        setError(e?.message || "โหลดลิงก์ Affiliate ไม่สำเร็จ");
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [uid]);

  if (loading) {
    return (
      <div className="py-4 text-center text-muted">
        <Spinner animation="border" size="sm" /> กำลังโหลดลิงก์...
      </div>
    );
  }

  if (!affiliate || affiliate?.status !== AFFILIATE_STATUSES.approved) {
    return <Alert variant="info">บัญชี Affiliate ยังไม่ได้รับการอนุมัติ</Alert>;
  }

  const onCopyLink = async (courseId, link) => {
    try {
      await copyTextToClipboard(link);
      setCopiedCourseId(courseId);
      window.setTimeout(() => {
        setCopiedCourseId((prev) => (prev === courseId ? "" : prev));
      }, 1800);
    } catch (e) {
      console.error(e);
      setError("คัดลอกลิงก์ไม่สำเร็จ กรุณาลองใหม่อีกครั้ง");
    }
  };

  return (
    <div>
      <h4 className="mb-1">Affiliate Links</h4>
      <div className="text-muted small mb-3">
        Code: <code>{affiliate.code}</code>
      </div>
      {error ? <Alert variant="danger">{error}</Alert> : null}

      {courses.length === 0 ? (
        <Card className="border">
          <Card.Body className="text-center text-muted py-5">ยังไม่มีคอร์สที่เปิด Affiliate</Card.Body>
        </Card>
      ) : (
        <div className="d-flex flex-column gap-3">
          {courses.map((course) => {
            const slug = course.slug || course.id;
            const link = `${SITE_ORIGIN}/courses/${slug}?ref=${encodeURIComponent(affiliate.code || "")}`;
            const cover = getCourseCover(course);
            return (
              <Card key={course.id} className="border">
                <Card.Body>
                  <div className="d-flex align-items-start gap-3">
                    <div
                      style={{
                        width: 112,
                        aspectRatio: "16/9",
                        borderRadius: 8,
                        overflow: "hidden",
                        flex: "0 0 112px",
                        background: "rgba(127,127,127,.12)",
                      }}
                    >
                      {cover ? (
                        <img
                          src={cover}
                          alt={course.title || "course cover"}
                          style={{ width: "100%", height: "100%", objectFit: "cover" }}
                          onError={(e) => {
                            e.currentTarget.style.display = "none";
                          }}
                        />
                      ) : null}
                    </div>

                    <div className="min-w-0 flex-grow-1">
                      <div className="d-flex align-items-start justify-content-between gap-3">
                        <div>
                          <div className="fw-semibold">{course.title || "(ไม่มีชื่อ)"}</div>
                          <div className="text-muted small">{course.subTitle || ""}</div>
                        </div>
                        <Badge bg="success">Affiliate</Badge>
                      </div>
                    </div>
                  </div>
                  <div className="mt-3 p-3 rounded" style={{ background: "rgba(127,127,127,.08)" }}>
                    <code className="text-break">{link}</code>
                  </div>
                  <div className="mt-2 d-flex align-items-center gap-2">
                    <Button
                      type="button"
                      size="sm"
                      variant="outline-primary"
                      onClick={() => onCopyLink(course.id, link)}
                    >
                      คัดลอกลิงก์
                    </Button>
                    {copiedCourseId === course.id ? (
                      <span className="text-success small">คัดลอกแล้ว</span>
                    ) : null}
                  </div>
                </Card.Body>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
